import { computed, ref } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import { useToast } from '@/_core/shared/ui/AppToast/useToast';
import { useSubmitLlmRating, useSubmitLlmComment } from './useSubmitLlmFeedback';
import type { TLlmFeedbackContentType, TLlmFeedbackRating } from '../models/types';

const SAMPLE_REPORT_SKIP_MESSAGE = 'For sample/test report, feedback is not sent to the server.';

type TOptimisticRatings = Partial<Record<TLlmFeedbackContentType, TLlmFeedbackRating>>;

export type TLlmFeedbackFlowOptions = {
  getContentId: () => string | undefined;
  getQueryKey: () => string[];
  getServerRating: (contentType: TLlmFeedbackContentType) => TLlmFeedbackRating | null;
  isContentAvailable: (contentType: TLlmFeedbackContentType) => boolean;
  /** When true, rating/comment are not sent to API; a log message is written instead. */
  skipSubmit?: () => boolean;
};

export function useLlmFeedbackFlow(options: TLlmFeedbackFlowOptions) {
  const { getContentId, getQueryKey, getServerRating, isContentAvailable, skipSubmit } = options;

  const queryClient = useQueryClient();
  const toast = useToast();

  const { mutateAsync: submitRating, isPending: isRatingPending } = useSubmitLlmRating();
  const { mutateAsync: submitComment, isPending: isCommentPending } = useSubmitLlmComment();

  const isCommentModalOpen = ref(false);
  const activeCommentType = ref<TLlmFeedbackContentType | null>(null);
  const isRatingSubmitting = ref(false);
  const optimisticRatings = ref<TOptimisticRatings>({});

  const isPending = computed(() =>
    [isRatingPending.value, isCommentPending.value, isRatingSubmitting.value].some(Boolean)
  );

  function getEffectiveRating(contentType: TLlmFeedbackContentType): TLlmFeedbackRating | null {
    return optimisticRatings.value[contentType] ?? getServerRating(contentType);
  }

  function setOptimisticRating(contentType: TLlmFeedbackContentType, rating: TLlmFeedbackRating) {
    optimisticRatings.value = { ...optimisticRatings.value, [contentType]: rating };
  }

  function openCommentModal(type: TLlmFeedbackContentType) {
    activeCommentType.value = type;
    isCommentModalOpen.value = true;
  }

  function closeCommentModal() {
    isCommentModalOpen.value = false;
    activeCommentType.value = null;
  }

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: getQueryKey() });
  }

  async function handleRate(contentType: TLlmFeedbackContentType, rating: TLlmFeedbackRating) {
    if (isRatingPending.value || isRatingSubmitting.value) return;

    const effectiveRating = getEffectiveRating(contentType);
    if (effectiveRating === rating) return;

    const contentId = getContentId();
    if (!contentId) return;

    if (!isContentAvailable(contentType)) {
      toast.error({ message: 'Summary is not available for feedback' });
      return;
    }

    // optimistic update — highlight icon immediately before API call
    setOptimisticRating(contentType, rating);

    isRatingSubmitting.value = true;
    try {
      if (skipSubmit?.()) {
        // eslint-disable-next-line no-console -- intentional log for sample report flow
        console.info(SAMPLE_REPORT_SKIP_MESSAGE);
        openCommentModal(contentType);
      } else {
        await submitRating({ contentType, contentId, rating });
        invalidate();
        openCommentModal(contentType);
      }
    } catch {
      // revert optimistic update on failure
      optimisticRatings.value = { ...optimisticRatings.value, [contentType]: undefined };
      toast.error({ message: 'Failed to submit feedback.' });
    } finally {
      isRatingSubmitting.value = false;
    }
  }

  async function handleCommentSubmit(comment: string) {
    if (isCommentPending.value) return;

    const trimmed = comment.trim();
    if (!activeCommentType.value) return;

    const contentId = getContentId();
    const contentType = activeCommentType.value;
    if (!contentId) return;

    if (!trimmed) {
      closeCommentModal();
      return;
    }

    isCommentModalOpen.value = false;

    try {
      if (skipSubmit?.()) {
        // eslint-disable-next-line no-console -- intentional log for sample report flow
        console.info(SAMPLE_REPORT_SKIP_MESSAGE);
        activeCommentType.value = null;
        toast.success({ message: 'Feedback submitted. Thank you!' });
      } else {
        await submitComment({ contentType, contentId, comment: trimmed });
        activeCommentType.value = null;
        toast.success({ message: 'Feedback submitted. Thank you!' });
      }
    } catch {
      toast.error({ message: 'Failed to submit feedback.' });
    }
  }

  return {
    isCommentModalOpen,
    activeCommentType,
    openCommentModal,
    closeCommentModal,
    handleRate,
    handleCommentSubmit,
    getEffectiveRating,
    isPending,
  };
}
